import * as React from 'react';

/** Stop the event only when it starts inside a list in the popup that can scroll. */
const releaseScrollableLists = event => {
  for (let node = event.target; node && node !== event.currentTarget; node = node.parentElement) {
    if (
      node.scrollHeight > node.clientHeight &&
      /auto|scroll/.test(getComputedStyle(node).overflowY)
    ) {
      event.stopPropagation();
      return;
    }
  }
};

/**
 * Ref for popup content (popover, combobox list, menu) that renders in its own portal.
 *
 * An open Dialog, Sheet or Drawer locks page scroll by cancelling every wheel and touch scroll
 * that reaches the document from outside its own content. A popup portalled to <body> is
 * outside it, so its lists (date picker time columns, option and command lists) could not
 * scroll. Events that start inside a scrollable list are kept away from the lock, so the list
 * scrolls natively; everywhere else in the popup the lock still applies.
 *
 * React `onWheel` / `onTouchMove` inside those lists won't fire; use the capture variants or a
 * native listener on the element. Pass the component's own `ref` to merge it.
 */
export function usePopupScrollRef(ref) {
  return React.useCallback(
    node => {
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
      if (!node) return undefined;
      node.addEventListener('wheel', releaseScrollableLists);
      node.addEventListener('touchmove', releaseScrollableLists);
      return () => {
        node.removeEventListener('wheel', releaseScrollableLists);
        node.removeEventListener('touchmove', releaseScrollableLists);
        if (typeof ref === 'function') ref(null);
        else if (ref) ref.current = null;
      };
    },
    [ref]
  );
}
